/* eslint-disable no-nested-ternary */
import React from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'components/_new/Tooltip/Tooltip';
import { Button } from 'components/_new/Button';

import { CreateGoalPayload, UpdateGoalPayload } from 'types/payloads/GoalPayload';
import { ActionGoalId, Goal, GoalAction, GoalStep } from 'types/Goal';

import { InputTextArea } from 'components/_new/InputTextArea';
import { IconSvg } from 'components/_new/Icon';
import { InputText } from 'components/_new/InputText';
import { ArrowList } from 'components/_new/List/ArrowList';
import { Box, BoxSection } from 'components/_new/Box';
import { ExpandablePanel } from 'components/_new/ExpandablePanel';
import getGoalRows from './components/getGoalRows';

interface Props {
	id: string;
	goalData: Goal;
	editedGoal: number;
	getGoals: () => void;
	updateGoal: (goalValue: UpdateGoalPayload) => void;
	saveNewGoal: (goalValue: CreateGoalPayload) => void;
}

type GoalFormObject = {
	goal: string;
	change: string;
	achivmentPath: string;
	priorities: string;
	notes: string;
	areaOfInfluence: string;
	deadlineAndReview: string;
	actions: Array<Pick<GoalAction, 'sequence' | 'text'> & { id?: number }>;
	steps: Array<Pick<GoalStep, 'sequence' | 'text'> & { id?: number }>;
};

type TooltipKeys = {
	goal: string;
	change: string;
	achivmentPath: string;
	priorities: string;
	notes: string;
	actions: string;
	steps: string;
	areaOfInfluence: string;
	deadlineAndReview: string;
};

const GoalForm = ({ id, goalData, editedGoal, updateGoal, saveNewGoal }: Props) => {
	const { goal, change, achivmentPath, priorities, notes, actions, steps, areaOfInfluence, deadlineAndReview } =
		goalData;
	const { t } = useTranslation();

	const { register, control, watch, handleSubmit } = useForm<GoalFormObject>({
		defaultValues: {
			goal,
			change,
			achivmentPath,
			priorities,
			notes,
			areaOfInfluence,
			deadlineAndReview,
			steps: steps?.map((s) => ({ id: s.id, sequence: s.sequence, text: s.text })) || [],
			actions: actions?.map((a) => ({ id: a.id, sequence: a.sequence, text: a.text })) || [],
		},
	});

	const {
		fields: stepFields,
		append: appendStep,
		remove: removeStep,
	} = useFieldArray({
		control,
		name: 'steps',
	});

	const {
		fields: actionFields,
		append: appendAction,
		remove: removeAction,
	} = useFieldArray({
		control,
		name: 'actions',
	});

	const [pendingStepIndex, setPendingStepIndex] = React.useState<number | null>(null);
	const [pendingActionIndex, setPendingActionIndex] = React.useState<number | null>(null);

	const watchedSteps = watch('steps');
	const watchedActions = watch('actions');

	const pendingStepHasText =
		pendingStepIndex !== null ? !!watchedSteps?.[pendingStepIndex]?.text?.trim().length : false;
	const pendingActionHasText =
		pendingActionIndex !== null ? !!watchedActions?.[pendingActionIndex]?.text?.trim().length : false;

	const rows = getGoalRows(goalData, t);

	const hasFilledExtendedField = rows?.some(({ type, isExtended, text, arr }) => {
		if (!isExtended) return false;
		if (type === 'actions' || type === 'steps') {
			return arr && arr.length > 0;
		}
		return text && text.trim().length > 0;
	});

	const basicRows = rows?.filter((r) => !r.isExtended);
	const extendedRows = rows?.filter((r) => r.isExtended);

	const handleSubmitForm = handleSubmit((values) => {
		const normalizedValues = {
			...values,
			actions: values.actions.map((item, index) => ({
				id: item.id || undefined,
				sequence: index + 1,
				text: item.text,
			})),
			steps: values.steps.map((item, index) => ({
				id: item.id || undefined,
				sequence: index + 1,
				text: item.text,
			})),
		};

		if (editedGoal === ActionGoalId.new) {
			saveNewGoal(normalizedValues);
		} else {
			updateGoal({ ...normalizedValues, id: editedGoal });
		}
	});

	const getTooltipKey = (type: keyof TooltipKeys) => {
		const typeToKey: TooltipKeys = {
			goal: 'agreedGoalTooltip',
			change: 'expectedChangeTooltip',
			achivmentPath: 'achivmentPathTooltip',
			priorities: 'prioritiesTooltip',
			notes: 'notesTooltip',
			actions: 'actionsTooltip',
			steps: 'stepsTooltip',
			areaOfInfluence: 'areaOfInfluenceTooltip',
			deadlineAndReview: 'deadlineAndReviewTooltip',
		};
		return `userPanel.goal.${typeToKey[type]}`;
	};

	const getTooltipContent = (type: keyof TooltipKeys): React.ReactNode => {
		const tooltip = t(getTooltipKey(type), { returnObjects: true }) as any;

		if (typeof tooltip === 'string') {
			return tooltip;
		}

		const mainListItems = [tooltip.el1, tooltip.el2, tooltip.el3, tooltip.el4, tooltip.el5, tooltip.el6].filter(
			Boolean
		);
		const exampleListItems = [tooltip.ex1, tooltip.ex2, tooltip.ex3, tooltip.ex4, tooltip.ex5, tooltip.ex6].filter(
			Boolean
		);

		return (
			<div className="flex flex-column gap-2">
				{tooltip.description && <div>{tooltip.description}</div>}
				{tooltip.listHeader && <div>{tooltip.listHeader}</div>}

				{mainListItems.length > 0 && (
					<ArrowList>
						{mainListItems.map((item: string) => (
							<li key={item}>{item}</li>
						))}
					</ArrowList>
				)}

				{tooltip.exampleHeader && <div>{tooltip.exampleHeader}</div>}
				{tooltip.example && <div>{tooltip.example}</div>}

				{exampleListItems.length > 0 && (
					<ArrowList>
						{exampleListItems.map((item: string) => (
							<li key={item}>{item}</li>
						))}
					</ArrowList>
				)}

				{tooltip.tip && <div>👉 {tooltip.tip}</div>}
			</div>
		);
	};
	const handleStartAddStep = () => {
		appendStep({ sequence: stepFields.length + 1, text: '' });
		setPendingStepIndex(stepFields.length);
	};

	const handleAcceptStep = () => {
		if (pendingStepIndex === null || !pendingStepHasText) {
			return;
		}
		setPendingStepIndex(null);
	};

	const handleCancelStep = () => {
		if (pendingStepIndex === null) {
			return;
		}
		removeStep(pendingStepIndex);
		setPendingStepIndex(null);
	};

	const handleRemoveStep = (index: number) => {
		removeStep(index);
		if (pendingStepIndex === null) {
			return;
		}
		if (index === pendingStepIndex) {
			setPendingStepIndex(null);
			return;
		}
		if (index < pendingStepIndex) {
			setPendingStepIndex(pendingStepIndex - 1);
		}
	};

	const handleStartAddAction = () => {
		appendAction({ sequence: actionFields.length + 1, text: '' });
		setPendingActionIndex(actionFields.length);
	};

	const handleAcceptAction = () => {
		if (pendingActionIndex === null || !pendingActionHasText) {
			return;
		}
		setPendingActionIndex(null);
	};

	const handleCancelAction = () => {
		if (pendingActionIndex === null) {
			return;
		}
		removeAction(pendingActionIndex);
		setPendingActionIndex(null);
	};

	const handleRemoveAction = (index: number) => {
		removeAction(index);
		if (pendingActionIndex === null) {
			return;
		}
		if (index === pendingActionIndex) {
			setPendingActionIndex(null);
			return;
		}
		if (index < pendingActionIndex) {
			setPendingActionIndex(pendingActionIndex - 1);
		}
	};

	const isPendingItem = (fieldType: 'steps' | 'actions', index: number): boolean => {
		if (fieldType === 'steps') {
			return pendingStepIndex === index;
		}
		return pendingActionIndex === index;
	};

	const renderDynamicFields = (type: 'steps' | 'actions', required?: boolean) => (
		<Box variant="darkblue">
			<BoxSection>
				{(type === 'steps' ? stepFields : actionFields).map((item, index) => (
					<div key={item.id} className="w-full flex gap-2 align-items-center">
						<span className="text-primary text-bold text-lg mr-2">{index + 1}.</span>
						<InputText
							{...register(`${type}.${index}.text` as const)}
							name={`${type}.${index}.text`}
							required={required}
							title=""
							className="w-full flex-1"
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									event.preventDefault();
									if (isPendingItem(type, index)) {
										if (type === 'steps') {
											handleAcceptStep();
										} else {
											handleAcceptAction();
										}
									}
								}
							}}
						/>
						{!isPendingItem(type, index) && (
							<Button
								variant="danger-text"
								onClick={(event) => {
									event.preventDefault();
									if (type === 'steps') {
										handleRemoveStep(index);
									} else {
										handleRemoveAction(index);
									}
								}}
								iconClassName="icon-container icon-container-sm"
								icon={() => <IconSvg name="trash" />}
								iconOnly
								label={t('actions.reject')}
							/>
						)}
					</div>
				))}
				{type === 'steps' ? (
					pendingStepIndex === null ? (
						<Button
							variant="primary-text"
							onClick={(event) => {
								event.preventDefault();
								handleStartAddStep();
							}}
							icon="plus"
							iconSet="fa"
							label={t('userPanel.goal.addItem')}
						/>
					) : (
						<div className="flex gap-2">
							<Button
								variant="primary-text"
								onClick={(event) => {
									event.preventDefault();
									handleAcceptStep();
								}}
								icon="check"
								iconSet="pi"
								label={t('actions.confirm')}
								disabled={!pendingStepHasText}
							/>
							<Button
								variant="danger-text"
								onClick={(event) => {
									event.preventDefault();
									handleCancelStep();
								}}
								icon="times"
								iconSet="pi"
								label={t('actions.reject')}
							/>
						</div>
					)
				) : pendingActionIndex === null ? (
					<Button
						variant="primary-text"
						onClick={(event) => {
							event.preventDefault();
							handleStartAddAction();
						}}
						icon="plus"
						iconSet="fa"
						label={t('userPanel.goal.addItem')}
					/>
				) : (
					<div className="flex gap-2 mb-2">
						<Button
							variant="primary-text"
							onClick={(event) => {
								event.preventDefault();
								handleAcceptAction();
							}}
							icon="check"
							iconSet="pi"
							label={t('actions.confirm')}
							disabled={!pendingActionHasText}
						/>
						<Button
							variant="danger-text"
							onClick={(event) => {
								event.preventDefault();
								handleCancelAction();
							}}
							icon="times"
							iconSet="pi"
							label={t('actions.reject')}
						/>
					</div>
				)}
			</BoxSection>
		</Box>
	);

	const renderFieldLabel = (type: keyof TooltipKeys, label: React.ReactNode) => {
		const tooltipTargetId = `goal-form-tooltip-${type}`;

		return (
			<div className="col-12 lg:col-4 p-2 flex align-items-center">
				<Button
					id={tooltipTargetId}
					variant="transparent"
					variantSize="sm"
					icon={() => <IconSvg name="question-mark-square" />}
					iconOnly
					noPad
					label="Info"
					iconClassName="icon-container icon-container-rm"
					className="text-primary mr-2 inline-flex"
				/>
				<Tooltip
					target={`#${tooltipTargetId}`}
					position="top"
					at="left top-10"
					my="left bottom"
					variant="white"
					openOnClick
				>
					{getTooltipContent(type)}
				</Tooltip>
				<span>{label}</span>
			</div>
		);
	};

	return (
		<form id={id} className="w-full" onSubmit={handleSubmitForm}>
			{basicRows?.map(({ label, required, type }) => (
				<div key={type} className="grid mt-2">
					{renderFieldLabel(type as keyof TooltipKeys, label)}
					<div className="col-12 lg:col-8">
						{type !== 'steps' && type !== 'actions' && (
							<InputTextArea
								{...register(type)}
								name={type}
								required={required}
								title=""
								className="w-full"
								placeholder={t(`userPanel.goal.${type}Placeholder`)}
								oneLine
								autoResize
							/>
						)}
						{type === 'steps' && renderDynamicFields('steps', required)}
						{type === 'actions' && renderDynamicFields('actions', required)}
					</div>
				</div>
			))}
			<div className="mt-2">
				<ExpandablePanel
					header={t('userPanel.goal.additionalCriteria')}
					expandedContentClassName="pt-2"
					expanded={hasFilledExtendedField}
				>
					{extendedRows?.map(({ label, required, type }) => (
						<div key={type} className="grid mt-2">
							{renderFieldLabel(type as keyof TooltipKeys, label)}
							<div className="col-12 lg:col-8">
								{type !== 'steps' && type !== 'actions' && (
									<InputTextArea
										{...register(type)}
										name={type}
										required={required}
										title=""
										className="w-full"
										placeholder={t(`userPanel.goal.${type}Placeholder`)}
										oneLine
										autoResize
									/>
								)}
								{type === 'steps' && renderDynamicFields('steps', required)}
								{type === 'actions' && renderDynamicFields('actions', required)}
							</div>
						</div>
					))}
				</ExpandablePanel>
			</div>
		</form>
	);
};

export default GoalForm;
